import { createContext, useContext, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';

import { State, useState, createState } from '@hookstate/core';

import { useHistory, useLocation } from 'react-router-dom';

import { NavigationContext } from '@layout/navigation/navigation';

import { getTasks, getTasksCount } from '@services/queries/tasks';
import { TasksLoading } from './Tasks.loading';
import { TasksRaw } from './Tasks.raw';

export type SortTasksTypes =
  | 't.title'
  | 't.description'
  | 't.priority'
  | 'u."name"'
  | 't.status'
  | 't."dueDate"'
  | undefined;

export type DirectionTaskstypes = 'ASC' | 'DESC' | undefined;

interface ITasksContext {
  search: State<string>;
  sort: State<SortTasksTypes>;
  direction: State<DirectionTaskstypes>;
  assignedToMe: State<boolean>;
  status: string;
}

export const TasksContext = createContext<ITasksContext>({
  search: createState<string>(''),
  sort: createState<SortTasksTypes>(undefined),
  direction: createState<DirectionTaskstypes>(undefined),
  assignedToMe: createState<boolean>(false),
  status: 'unverified',
});

const useInfiniteQueryParams = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const TasksQuery = () => {
  const { studioId, userId } = useContext(NavigationContext);

  const history = useHistory();

  const search = useState<string>('');
  const sort = useState<SortTasksTypes>(undefined);
  const direction = useState<DirectionTaskstypes>(undefined);
  const assignedToMe = useState<boolean>(false);

  const query = useInfiniteQueryParams();

  const status = query.get('tab') || undefined;

  if (!status || !['all', 'todo', 'inprogress', 'done'].includes(status)) {
    history.push(`/tasks?tab=all`);
  }

  const tasksQuery = useInfiniteQuery(
    [
      'tasks',
      search.get(),
      status,
      sort.value,
      direction.value,
      assignedToMe.value,
    ],
    async ({ pageParam = 1 }) => {
      return getTasks({
        studioId,
        limit: 20,
        page: pageParam,
        status: status === 'all' ? undefined : status,
        search: search.get(),
        assigneeId: assignedToMe.value ? userId : undefined,
        sort: sort.value,
        direction: direction.value,
      });
    },
    {
      getPreviousPageParam: undefined,
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const tasksCountQuery = useQuery(
    ['tasks', search.get(), 'count', assignedToMe.value],
    async () => {
      return getTasksCount({
        studioId,
        search: search.get(),
        assigneeId: assignedToMe.value ? userId : undefined,
      });
    },
  );

  if (!status) {
    return <TasksLoading />;
  }

  return (
    <TasksContext.Provider
      value={{
        search,
        sort,
        direction,
        status,
        assignedToMe,
      }}
    >
      <TasksRaw
        tasksQuery={tasksQuery.data}
        tasksCountQuery={tasksCountQuery}
        fetchNextPage={tasksQuery.fetchNextPage}
        isError={tasksQuery.isError}
        isLoading={tasksQuery.isLoading}
      />
    </TasksContext.Provider>
  );
};

export { TasksQuery };
